// WebSocket client: joins a room, buffers server snapshots, and exposes a
// time-shifted interpolated view for the renderer (no client prediction —
// ARCHITECTURE §8.5: at hold-a-direction cadence, ~100 ms is imperceptible).

import {
  buildGrid,
  snapshotPlayersById,
  type Bouncer,
  type DirectionPref,
  type HexGrid,
  type PlayerId,
  type ServerMessage,
  type ClientMessage,
  type WorldSnapshot,
  type SnapshotPlayer,
} from '@evox/shared';
import { edgeParamToPx, nearestCopy, type Px } from '../render/geometry.js';

/** How far behind real time we render (~1.8 snapshot intervals at 15 Hz). */
const RENDER_DELAY_MS = 120;
/** Snapshots kept in the buffer (a few seconds of history is plenty). */
const BUFFER_SIZE = 8;

export interface ViewPlayer {
  id: PlayerId;
  name: string;
  color: string;
  px: Px;
  speed: number;
  health: number;
  direction: 'forward' | 'backward';
  dirPref: 'left' | 'right';
  invuln: boolean;
  /** Unit vector of current travel for the directional glow. */
  heading: Px;
}

export interface View {
  tick: number;
  players: ViewPlayer[];
  pellets: { id: string; px: Px }[];
}

export interface Connection {
  readonly grid: HexGrid;
  readonly bouncers: Bouncer[];
  readonly localPlayerId: PlayerId;
  readonly resumeToken: string;
  /** Enqueue one future turn on the server (tap / hold-accrued). */
  sendTurn(dir: DirectionPref): void;
  /** Interpolated world view for rendering at wall-clock `nowMs`. */
  getView(nowMs: number): View | null;
  /** Latest raw snapshot (for the leaderboard — no interpolation needed). */
  latest(): WorldSnapshot | null;
  /** Round-trip time estimate in ms (last measured). */
  rttMs(): number;
  close(): void;
  onClose(cb: (reason: string) => void): void;
}

export interface Buffered {
  recvAt: number;
  snap: WorldSnapshot;
}

/**
 * Pick the snapshot pair bracketing `target` time, with the blend factor.
 *
 * Behavior at the boundaries (the 2026-06-12 "teleport-and-back" fix):
 * - target beyond the NEWEST snapshot (snapshots stalled for a moment):
 *   HOLD the newest — never fall back to older pairs. The previous search
 *   walked downward and could land on the oldest pair, rendering a position
 *   up to half a second in the past, then snapping back on the next frame.
 * - target before the OLDEST snapshot: hold the oldest.
 */
export function pickBracket(
  buffer: readonly Buffered[],
  target: number,
): { older: Buffered; newer: Buffered; alpha: number } {
  const newest = buffer[buffer.length - 1]!;
  const oldest = buffer[0]!;
  if (target >= newest.recvAt) return { older: newest, newer: newest, alpha: 1 };
  if (target <= oldest.recvAt) return { older: oldest, newer: oldest, alpha: 1 };
  // Walk upward to the first snapshot at-or-after target.
  for (let k = 1; k < buffer.length; k++) {
    if (buffer[k]!.recvAt >= target) {
      const older = buffer[k - 1]!;
      const newer = buffer[k]!;
      const span = newer.recvAt - older.recvAt;
      const alpha = span > 0 ? (target - older.recvAt) / span : 1;
      return { older, newer, alpha };
    }
  }
  /* istanbul ignore next -- unreachable given the boundary checks */
  return { older: newest, newer: newest, alpha: 1 };
}

export function connect(url: string, name: string, resume?: string): Promise<Connection> {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url);
    const buffer: Buffered[] = [];
    let closeCbs: ((reason: string) => void)[] = [];
    let rtt = 0;
    let pingTimer: number | null = null;
    let settled = false;

    ws.onerror = () => {
      if (!settled) reject(new Error('connection failed'));
    };

    ws.onclose = () => {
      if (pingTimer !== null) window.clearInterval(pingTimer);
      for (const cb of closeCbs) cb('closed');
      closeCbs = [];
    };

    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data as string) as ServerMessage;
      switch (msg.type) {
        case 'welcome': {
          const grid = buildGrid(msg.dims);
          const conn = makeConnection(ws, grid, msg.bouncers, msg.playerId, msg.resumeToken, buffer, {
            getRtt: () => rtt,
            addCloseCb: (cb) => closeCbs.push(cb),
          });
          pingTimer = window.setInterval(() => {
            send(ws, { type: 'ping', sentAt: performance.now() });
          }, 3000);
          settled = true;
          resolve(conn);
          break;
        }
        case 'snapshot': {
          const recvAt = performance.now();
          const prev = buffer[buffer.length - 1];
          if (prev !== undefined && recvAt - prev.recvAt > 250) {
            // Diagnostic for smoothness work: a stalled stream is the usual
            // culprit behind visual hiccups. Visible in the browser console.
            console.debug(`[hexline] snapshot gap: ${Math.round(recvAt - prev.recvAt)}ms`);
          }
          buffer.push({ recvAt, snap: msg.snapshot });
          if (buffer.length > BUFFER_SIZE) buffer.shift();
          break;
        }
        case 'pong':
          rtt = performance.now() - msg.sentAt;
          break;
        case 'goodbye':
          for (const cb of closeCbs) cb(msg.reason);
          closeCbs = [];
          ws.close();
          break;
      }
    };

    ws.onopen = () => {
      const join: ClientMessage = { type: 'join', name, ...(resume ? { resume } : {}) };
      send(ws, join);
    };
  });
}

function send(ws: WebSocket, msg: ClientMessage): void {
  if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
}

function makeConnection(
  ws: WebSocket,
  grid: HexGrid,
  bouncers: Bouncer[],
  localPlayerId: PlayerId,
  resumeToken: string,
  buffer: Buffered[],
  extras: { getRtt: () => number; addCloseCb: (cb: (reason: string) => void) => void },
): Connection {
  return {
    grid,
    bouncers,
    localPlayerId,
    resumeToken,
    sendTurn(dir) {
      send(ws, { type: 'turn', dir });
    },
    latest() {
      return buffer.length > 0 ? buffer[buffer.length - 1]!.snap : null;
    },
    rttMs: extras.getRtt,
    getView(nowMs) {
      if (buffer.length === 0) return null;
      const { older, newer, alpha } = pickBracket(buffer, nowMs - RENDER_DELAY_MS);
      return interpolate(grid, older.snap, newer.snap, alpha);
    },
    close() {
      ws.close();
    },
    onClose(cb) {
      extras.addCloseCb(cb);
    },
  };
}

function interpolate(
  grid: HexGrid,
  older: WorldSnapshot,
  newer: WorldSnapshot,
  alpha: number,
): View {
  const olderById = snapshotPlayersById(older);
  const players: ViewPlayer[] = [];
  for (const np of newer.players) {
    const op = olderById.get(np.id);
    players.push(viewPlayer(grid, op, np, alpha));
  }
  return {
    tick: newer.tick,
    players,
    pellets: newer.pellets.map((p) => ({ id: p.id, px: edgeParamToPx(grid, p.edgeId, p.t) })),
  };
}

function viewPlayer(
  grid: HexGrid,
  op: SnapshotPlayer | undefined,
  np: SnapshotPlayer,
  alpha: number,
): ViewPlayer {
  const newPx = edgeParamToPx(grid, np.edgeId, np.t);
  let px = newPx;
  if (op !== undefined) {
    // Interpolate in pixel space; unwrap the new position to the copy nearest
    // the old one so seam crossings stay continuous. Edge transitions cut the
    // corner slightly for one snapshot interval — invisible at dot scale.
    const oldPx = edgeParamToPx(grid, op.edgeId, op.t);
    const target = nearestCopy(newPx, oldPx, grid.dims);
    px = { x: oldPx.x + (target.x - oldPx.x) * alpha, y: oldPx.y + (target.y - oldPx.y) * alpha };
  }
  // Heading: direction of travel along the current edge.
  const a = edgeParamToPx(grid, np.edgeId, np.direction === 'forward' ? 0.4 : 0.6);
  const b = edgeParamToPx(grid, np.edgeId, np.direction === 'forward' ? 0.6 : 0.4);
  const hx = b.x - a.x;
  const hy = b.y - a.y;
  const hl = Math.hypot(hx, hy) || 1;
  return {
    id: np.id,
    name: np.name,
    color: np.color,
    px,
    speed: op ? op.speed + (np.speed - op.speed) * alpha : np.speed,
    health: op ? op.health + (np.health - op.health) * alpha : np.health,
    direction: np.direction,
    dirPref: np.dirPref,
    invuln: np.invuln,
    heading: { x: hx / hl, y: hy / hl },
  };
}
